import { CommonModule } from '@angular/common'
import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core'
import { FlexModule } from '@angular/flex-layout'
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms'
import { MatButtonModule } from '@angular/material/button'
import { MatCheckboxModule } from '@angular/material/checkbox'
import { MatDialog } from '@angular/material/dialog'
import { MatFormFieldModule } from '@angular/material/form-field'
import { MatIconModule } from '@angular/material/icon'
import { MatInputModule } from '@angular/material/input'
import { MatMenuModule } from '@angular/material/menu'
import { MatTableModule } from '@angular/material/table'
import { MatTooltipModule } from '@angular/material/tooltip'
import { PipesModule } from '@mmx/pipes'
import {
  AppointmentModel,
  AUTHORIZATION_STATUS,
  AuthorizationModel,
  Clinic,
  DirectivesModule,
  OutdatedFlags,
  PatientInsurance,
  PatientUploadedFile,
  Permission,
} from '@mmx/shared'
import { TranslateModule, TranslateService } from '@ngx-translate/core'
import { BehaviorSubject, combineLatest, concatWith, defer, distinctUntilChanged, filter, map, of, Subject, takeUntil } from 'rxjs'

import { CopyTextModule } from '../copy-text'
import { PatientAuthHistoryDialogComponent } from '../patient-auth-history-dialog'
import { PatientFileViewComponent } from '../patient-file-view'
import { StatusIconsModule } from '../status-icons'

@Component({
  selector: 'mmx-authorization-table',
  templateUrl: './authorization-table.component.html',
  styleUrls: ['./authorization-table.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    CopyTextModule,
    FlexModule,
    PipesModule,
    DirectivesModule,
    TranslateModule,
    StatusIconsModule,
    MatTableModule,
    MatButtonModule,
    MatCheckboxModule,
    MatFormFieldModule,
    MatInputModule,
    MatTooltipModule,
    MatMenuModule,
    MatIconModule,
    ReactiveFormsModule,
  ],
  standalone: true,
})

export class AuthorizationTableComponent implements OnDestroy, OnInit {
  @Input()
  public set authorizations(auths: AuthorizationModel[]) {
    this.authorizations$.next(auths)
  }

  @Input()
  public set appointment(appointment: AppointmentModel | undefined) {
    this.appointment$.next(appointment)
  }

  public get appointment(): AppointmentModel | undefined {
    return this.appointment$.getValue()
  }

  @Input()
  public insurances: PatientInsurance[] = []

  @Input()
  public clinic: Clinic

  @Input()
  public canPullAuthStatus: boolean

  @Input()
  public showRemaining = false

  @Input()
  public showProcedureQuantity = false

  @Output()
  public deleteAuthorization = new EventEmitter<AuthorizationModel>()

  @Output()
  public saveAuthorization = new EventEmitter<AuthorizationModel>()

  @Output()
  public editAuthorization = new EventEmitter<AuthorizationModel>()

  @Output()
  public pullAuthorizationStatus = new EventEmitter<AuthorizationModel>()

  @Output()
  public cancelPullAuthorizationStatus = new EventEmitter<AuthorizationModel>()

  @Output()
  public filterActiveChange = new EventEmitter<boolean>()

  public readonly displayedColumns: string[] = [
    'appointment',
    'procedures',
    'diagnoses',
    'effectiveDate',
    'authorizationStatusText',
    'number',
    'insurance',
    'facility',
    'note',
    'edit',
  ]

  public readonly Permission = Permission
  public readonly AUTHORIZATION_STATUS = AUTHORIZATION_STATUS
  private onDestroy$ = new Subject<void>()

  public authorizations$ = new BehaviorSubject<AuthorizationModel[]>([])
  private appointment$ = new BehaviorSubject<AppointmentModel | undefined>(undefined)
  public filtersForm = new FormGroup({
    cpt: new FormControl('', { nonNullable: true }),
  })

  private cptFilter$ = defer(() => of(this.filtersForm.controls.cpt.getRawValue()).pipe(
    concatWith(this.filtersForm.controls.cpt.valueChanges),
  ))

  public hasActiveFilters$ = this.cptFilter$.pipe(
    map((cptFilter) => !!this.normalizeCptCode(cptFilter)),
  )

  public authorizationsToShow$ = combineLatest([
    this.authorizations$,
    this.appointment$,
    this.cptFilter$,
  ]).pipe(
    map(([authorizations, appointment, cptFilter]) => {
      const appointmentCptCodes = appointment?.getCptCodes() ?? []
      const appointmentStartDate = appointment?.startAt
      const appointmentFacilityNpi = appointment?.location?.provider?.id
      const normalizedCptFilter = this.normalizeCptCode(cptFilter)

      const extendedAuthorizations = authorizations.map((auth) => {
        const outdatedFlags: OutdatedFlags[] = []
        if (
          !!appointmentCptCodes.length &&
          auth.procedureCodes?.length &&
          !appointmentCptCodes.every((code) => auth.procedureCodes.includes(code))
        ) {
          outdatedFlags.push(OutdatedFlags.MissingProcedures)
        }

        if (auth.dateFrom && auth.dateTo && appointmentStartDate) {
          if (!appointmentStartDate.isBetween(auth.dateFrom, auth.dateTo, 'day', '[]')) {
            outdatedFlags.push(OutdatedFlags.ConflictDates)
          }
        }

        if (appointmentFacilityNpi && auth.facilityNPI && appointmentFacilityNpi !== auth.facilityNPI) {
          outdatedFlags.push(OutdatedFlags.WrongFacility)
        }

        auth.outdatedFlags = outdatedFlags.length ? outdatedFlags : undefined

        return auth
      })

      return extendedAuthorizations.filter((authorization) => {
        if (normalizedCptFilter && !this.matchesCptFilter(authorization, normalizedCptFilter)) {
          return false
        }

        return true
      })
    }),
  )

  constructor(
    private dialog: MatDialog,
    private translate: TranslateService,
  ) { }

  public ngOnInit() {
    this.hasActiveFilters$
      .pipe(
        distinctUntilChanged(),
        takeUntil(this.onDestroy$),
      )
      .subscribe((hasActiveFilters) => {
        this.filterActiveChange.emit(hasActiveFilters)
      })
  }

  public ngOnDestroy() {
    this.onDestroy$.next()
    this.onDestroy$.complete()
  }

  public getCheckboxLabel(authorization: AuthorizationModel): string {
    return this.isCheckboxDisabled(authorization)
      ? 'patient-details.authorization-card.table.checkbox.disabled-tooltip'
      : this.checkboxChecked(authorization)
        ? 'patient-details.authorization-card.table.checkbox.checked-tooltip'
        : 'patient-details.authorization-card.table.checkbox.tooltip'
  }

  public isCheckboxDisabled(authorization: AuthorizationModel): boolean {
    return !this.checkboxChecked(authorization) && authorization.status === this.AUTHORIZATION_STATUS.NONE
  }

  public checkboxChecked(authorization: AuthorizationModel) {
    return authorization.isAssociatedWithAppointment(this.appointment)
  }

  public checkboxChanged(authorization: AuthorizationModel) {
    // do not allow NONE status Authorizations to be linked up to anything, the status is meaningless
    if (authorization.status === AUTHORIZATION_STATUS.NONE) {
      return
    }

    // rely on consistent logic to determine if checkbox is already checked
    const associated = authorization.isAssociatedWithAppointment(this.appointment)

    /**
     * If the appointment is associated to a Patient Account,
     * then associate the Patient Account with the authorization.
     * Otherwise, we can only associate the Appointment directly to the Authorization.
     */
    if (this.appointment.accountId) {
      if (associated) {
        authorization.appointmentIds.delete(this.appointment.id)
        authorization.accountIds.delete(this.appointment.accountId)
      } else {
        authorization.accountIds.add(this.appointment.accountId)
        authorization.appointmentIds.add(this.appointment.id)
      }

      this.saveAuthorization.emit(authorization)
    } else {
      if (associated) {
        authorization.appointmentIds.delete(this.appointment.id)
      } else {
        authorization.appointmentIds.add(this.appointment.id)
      }

      this.saveAuthorization.emit(authorization)
    }
  }

  public isOutdated(authorization: AuthorizationModel) {
    const auth = this.appointment?.authorizations.find(({ id }) => id === authorization.id)
    return auth?.outdated ?? false
  }

  public clearFilters() {
    this.filtersForm.controls.cpt.reset('')
  }

  public viewFile(authorization: AuthorizationModel, file: PatientUploadedFile) {
    this.dialog.open(PatientFileViewComponent, {
      panelClass: 'dialog-form',
      width: file.extension === '.pdf' ? '2000px' : '500px',
      data: file,
    })
      .afterClosed()
      .pipe(
        takeUntil(this.onDestroy$),
        filter(Boolean),
      )
      .subscribe(() => {
        const index = authorization.attachments.findIndex((f) => f === file)
        authorization.attachments.splice(index, 1)
      })
  }

  public openRawResponse(auth: AuthorizationModel) {
    const newWindow = window.open()
    if (newWindow) {
      const html = `<pre><code>${JSON.stringify(
        {
          id: auth.id,
          response: auth.fullAuthorizationObject,
        },
        null,
        2,
      )}</code></pre>`
      newWindow.document.write(html)
    } else {
      alert('A popup blocker prevented us from opening the raw response')
    }
  }

  public showHistory(authorization: AuthorizationModel) {
    this.dialog.open(PatientAuthHistoryDialogComponent, {
      data: { authorization },
      panelClass: 'dialog-form',
    })
      .afterClosed()
      .pipe(
        takeUntil(this.onDestroy$),
        filter(Boolean),
      )
      .subscribe((authorization: AuthorizationModel) => {
        this.editAuthorization.emit(authorization)
      })
  }

  public getInsurance(authorization: AuthorizationModel): PatientInsurance | undefined {
    return this.insurances.find(({ id }) => id === authorization.insuranceId)
  }

  public pullAuthStatus(authorization: AuthorizationModel) {
    if (!this.canPullStatus(authorization).result) {
      return
    }

    this.pullAuthorizationStatus.emit(authorization)
  }
  public cancelPullAuthStatus(authorization: AuthorizationModel) {
    this.cancelPullAuthorizationStatus.emit(authorization)
  }

  public canPullStatus(authorization: AuthorizationModel): { result: boolean, reason?: string } {
    if (!this.canPullAuthStatus) {
      return {
        result: false,
        reason: 'no-api',
      }
    }

    if (authorization.awaitingAsyncPullResult) {
      return {
        result: false,
        reason: 'pending',
      }
    }

    if (!authorization.referenceNumber && !authorization.number) {
      return {
        result: false,
        reason: 'no-identifier',
      }
    }

    if (authorization.isComplete) {
      return {
        result: false,
        reason: 'complete',
      }
    }

    return { result: true }
  }

  public getPullStatusTooltip(canPull: { result: boolean, reason?: string }): string {
    if (canPull.result || !canPull.reason) {
      return ''
    }

    return this.translate.instant(`patient-details.authorization-card.pull-status-tooltip.${canPull.reason}`)
  }

  private matchesCptFilter(authorization: AuthorizationModel, cptFilter: string): boolean {
    return this.getAuthorizationCptCodes(authorization).some((code) => code.includes(cptFilter))
  }

  private getAuthorizationCptCodes(authorization: AuthorizationModel): string[] {
    return (authorization.procedureCodes ?? []).map((code) => this.normalizeCptCode(code))
  }

  private normalizeCptCode(value?: string): string {
    return value?.trim().toUpperCase() ?? ''
  }

  private get expirationWarningDays(): number {
    return this.clinic?.modulesPreferences?.authorizationExpirationWarningDays ?? 0
  }

  private get showAuthExpirationWarning(): boolean {
    return this.clinic?.modulesPreferences?.authorizationExpirationWarning ?? false
  }

  public showAuthorizationExpiringSoon(auth: AuthorizationModel): boolean {
    if (
      !this.showAuthExpirationWarning ||
      !auth.expiredAppointmentIds?.size ||
      !this.appointment?.startAt ||
      !auth.endAt
    ) {
      return false
    }

    const now = new Date()
    const appointmentStart = this.appointment.startAt.toDate()
    const authEnd = auth.endAt.toDate()

    const windowEnd = new Date(
      now.getTime() + this.expirationWarningDays * 24 * 60 * 60 * 1000
    )

    // Auth end date is in the future AND within the warning window
    if (authEnd > now) {
      return authEnd <= windowEnd
    }

    // Auth is expired AND appointment is in the future AND within the warning window
    return appointmentStart > now && appointmentStart <= windowEnd
  }

  public getAuthorizationExpirationLabel(auth: AuthorizationModel): string {
    if (!auth?.endAt) {
      return 'patient-details.authorization-card.expired-with-uncovered-appointments'
    }

    const now = new Date()
    const authEnd = auth.endAt.toDate()

    return authEnd >= now
      ? 'patient-details.authorization-card.expires-with-uncovered-appointments'
      : 'patient-details.authorization-card.expired-with-uncovered-appointments'
  }

  public getAuthorizationExpirationParams(auth: AuthorizationModel): { days?: number, uncoveredAppointments: number } {
    if (!auth.endAt) {
      return { uncoveredAppointments: auth.expiredAppointmentIds?.size ?? 0 }
    }
    const now = new Date()
    const authEnd = auth.endAt.toDate()

    const uncoveredAppointment = auth.expiredAppointmentIds?.size ?? 0

    if (authEnd >= now) {
      const diffMs = authEnd.getTime() - now.getTime()
      const daysLeft = Math.ceil(diffMs / (1000 * 60 * 60 * 24))

      return {
        days: daysLeft,
        uncoveredAppointments: uncoveredAppointment,
      }
    }

    return {
      uncoveredAppointments: uncoveredAppointment,
    }
  }

}
